import React, { Fragment, useState, useEffect } from 'react'
import { Modal, Table, Collapse, Button, Container } from 'react-bootstrap'
import Link from 'next/link'
import getConfig from 'next/config'
import styles from './CookieSettingModal.module.scss'
import classNames from 'classnames'
import Cookies from 'js-cookie'
import { useTranslation } from 'next-i18next'

const { publicRuntimeConfig } = getConfig()

const CookieSettingModal = ({ setShow, ...props }) => {
  const { t } = useTranslation('common')

  const [expandCookies, setExpandCookies] = useState([])
  const originURL = window ? window?.origin : ''

  const [cookieConsents, setCookieConsents] = useState([])
  const [showBanner, setShowBanner] = useState(false)

  const defaults = {
    expiresIn: 396 // As per KP5-491 REQ#6: Recommended to keep record for 13 months
  }

  useEffect(() => {
    setShowBanner(Cookies.get('kpAcceptedCookie') !== '1')

    let savedCookies = {}
    if (localStorage.getItem('cookie_consent')) {
      savedCookies = JSON.parse(localStorage.getItem('cookie_consent'))
    }

    setCookieConsents([
      {
        key: 'cookies-essential',
        isChecked: savedCookies['cookies-essential']?.isChecked || true,
        isDisabled: savedCookies['cookies-essential']?.isDisabled || true,
        title: t('Essential_Cookies'),
        description: t('These_cookies_are_strictly_text'),
        sites: [
          {
            company: 'Samsung Electronics Co., Ltd.',
            domain: ['.samsungknox.com', originURL].join(', ')
          }
        ]
      },
      {
        key: 'cookies-advertising',
        isChecked: savedCookies['cookies-advertising']?.isChecked || false,
        isDisabled: savedCookies['cookies-advertising']?.isDisabled || false,
        title: t('Advertising_Targeting_Cookies'),
        description: t('These_cookies_collect_information_text'),
        sites: [
          {
            company: 'Samsung Electronics Co., Ltd.',
            domain: [originURL].join(', ')
          },
          { company: 'Google LLC', domain: ['.google.com'].join(', ') },
          {
            company: 'Meta Platforms, Inc.',
            domain: ['.facebook.com'].join(', ')
          },
          {
            company: 'LinkedIn Corporation',
            domain: ['.linkedin.com'].join(', ')
          }
        ]
      }
    ])
  }, [])

  const onToggleCookie = (e) => {
    const consentKey = e.target.id
    const tempArray = [...cookieConsents]

    let currentConsent = tempArray.find((arr) => arr.key === consentKey)
    currentConsent.isChecked = e.target.checked

    setCookieConsents(tempArray)
  }

  const onClickViewCookies = (e, consent) => {
    e.preventDefault()
    const consentKey = consent.key
    let tempArray = [...expandCookies]
    if (expandCookies.includes(consentKey))
      setExpandCookies(tempArray.filter((item) => item !== consentKey))
    else setExpandCookies([...tempArray, consentKey])
  }

  const removeAdCookie = () => {
    Cookies.remove('cookies-advertising', { path: '/', domain: '.samsungknox.com' })
  }

  const acceptCookie = () => {
    Cookies.set('kpAcceptedCookie', 1, { expires: defaults.expiresIn, domain: '.samsungknox.com' })
  }

  const saveCookieDetails = () => {
    let cookie_consent = {}
    cookieConsents.map((consent) => {
      cookie_consent[consent.key] = {
        isChecked: consent?.isChecked,
        isDisabled: consent.isDisabled
      }

      if (consent.key === 'cookies-advertising' && !consent.isChecked) removeAdCookie()
      else if (consent.key !== 'cookies-essential') {
        Cookies.set(consent.key, consent.isChecked, {
          expires: defaults.expiresIn,
          domain: '.samsungknox.com'
        })
      }
    })
    localStorage.setItem('cookie_consent', JSON.stringify(cookie_consent))
    acceptCookie()
    setShowBanner(false)
  }

  const onSavePreferenceAndClose = () => {
    if (props.onHide) props.onHide()
    saveCookieDetails()
  }

  const onShowModal = () => {
    setShow(true)
  }

  const onContinueWithoutAccepting = (e) => {
    e.preventDefault()
    setCookieConsents([
      {
        key: 'cookies-essential',
        isChecked: true,
        isDisabled: true,
        title: t('Essential_Cookies'),
        description: t('These_cookies_are_strictly_text'),
        sites: [
          {
            company: 'Samsung Electronics Co., Ltd.',
            domain: ['.samsungknox.com', originURL].join(', ')
          }
        ]
      },
      {
        key: 'cookies-advertising',
        isChecked: false,
        isDisabled: false,
        title: t('Advertising_Targeting_Cookies'),
        description: t('These_cookies_collect_information_text'),
        sites: [
          {
            company: 'Samsung Electronics Co., Ltd.',
            domain: [originURL].join(', ')
          },
          { company: 'Google LLC', domain: ['.google.com'].join(', ') },
          {
            company: 'Meta Platforms, Inc.',
            domain: ['.facebook.com'].join(', ')
          },
          {
            company: 'LinkedIn Corporation',
            domain: ['.linkedin.com'].join(', ')
          }
        ]
      }
    ])
    saveCookieDetails()
  }

  return (
    <>
      <Modal {...props} className={styles.cookieModal}>
        <Modal.Header closeButton>{t('Manage_your_cookies')}</Modal.Header>
        <Modal.Body>
          <p>{t('Cookies_are_small_text_files_text')}</p>
          <p className="mb-4">
            {t('For_more_information_refer_to_text')}{' '}
            <Link href={`${publicRuntimeConfig.kp4BaseUrl}/cookie-policy`}>
              <a target="_blank">{t('Cookie_Policy')}</a>
            </Link>
            .
          </p>

          {cookieConsents.map((consent) => {
            return (
              <Fragment key={consent.key}>
                <div className={styles.consentItem}>
                  <div>
                    <p className={styles.consentItemTitle}>{consent.title}</p>
                    <p className={styles.consentItemDescription}>
                      {consent.description}{' '}
                      <Link href="#">
                        <a aria-hidden onClick={(e) => onClickViewCookies(e, consent)}>
                          {t('View_cookies')}
                        </a>
                      </Link>
                    </p>
                  </div>
                  <label className={styles.toggleSwitch}>
                    <input
                      id={consent.key}
                      name="cookie_category"
                      type="checkbox"
                      checked={consent.isChecked}
                      disabled={consent.isDisabled}
                      onChange={onToggleCookie}
                    />
                    <span
                      className={classNames(styles.slider, consent.isChecked && styles.checked)}
                    >
                      <span className={classNames(styles.sliderText)}>
                        {consent.isChecked ? t('Yes') : t('No')}
                      </span>
                    </span>
                  </label>
                </div>
                <Collapse in={expandCookies.includes(consent.key)}>
                  <div className={classNames(styles.consentSites)}>
                    <Table responsive>
                      <thead>
                        <tr>
                          <th>Company</th>
                          <th>Domain</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consent.sites.map((site, i) => (
                          <tr key={i}>
                            <td>{site.company}</td>
                            <td>{site.domain}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Collapse>
              </Fragment>
            )
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-primary" onClick={onSavePreferenceAndClose}>
            {t('Save_preferences_and_close')}
          </Button>
        </Modal.Footer>
      </Modal>
      {showBanner && (
        <div className={styles.cookieConsent}>
          <Container>
            <div>
              <div className={styles.titleWrapper}>
                <p className={styles.consentTitle}>{t('Samsung_and_Cookies')}</p>
                <a href="#" onClick={onContinueWithoutAccepting}>
                  {t('Continue_without_accepting')}
                </a>
              </div>
              <p>{t('Samsung_Electronics_and_its_text')}</p>
              <p>
                {t('If_you_are_happy_for_tech_text')}{' '}
                <Link href={`${publicRuntimeConfig.kp4BaseUrl}/cookie-policy`}>
                  <a target="_blank">{t('Cookie_Policy')}</a>
                </Link>{' '}
                {t('and_our')}{' '}
                <Link href="/privacy-policy">
                  <a target="_blank">{t('Privacy_Policy')}</a>
                </Link>
                .
              </p>
            </div>
            <div className={styles.buttonWrapper}>
              <a href="#" onClick={() => onShowModal()}>
                {t('Configure')}
              </a>
              <Button className="btn-outline-primary">{t('Accept_all')}</Button>
            </div>
          </Container>
        </div>
      )}
    </>
  )
}

export default CookieSettingModal
